import { useState, useEffect } from "react";
import { browserCache } from "@/utils/cache";
import { useReadJsonFile } from "./useReadJsonFile";
import termsJsonData from "@/assets/docs/test-terms-and-conditions.json";
import privacyJsonData from "@/assets/docs/test-privacy-policy.json";

export interface UseJsonDocContentOptions {
  cacheTimeMinutes?: number;
  disableAutoRefresh?: boolean;
}

interface JsonContentState {
  terms: {
    html: string;
    lastUpdated?: string;
  };
  privacy: {
    html: string;
    lastUpdated?: string;
  };
  isLoading: boolean;
  error: Error | null;
}

export function useJsonDocContent(options: UseJsonDocContentOptions = {}) {
  // Process the imported JSON files
  const {
    content: termsContent,
    isLoading: termsLoading,
    error: termsError,
  } = useReadJsonFile(termsJsonData);
  const {
    content: privacyContent,
    isLoading: privacyLoading,
    error: privacyError,
  } = useReadJsonFile(privacyJsonData);

  const [jsonContent, setJsonContent] = useState<JsonContentState>({
    terms: { html: "" },
    privacy: { html: "" },
    isLoading: true,
    error: null,
  });

  const [fetchAttempt, setFetchAttempt] = useState(0);
  const cacheTimeMinutes = options.cacheTimeMinutes || 30;
  const disableAutoRefresh = options.disableAutoRefresh || false;

  // Function to force clear cache and refetch
  const clearCache = () => {
    browserCache.set("json_doc_content", null);
    setFetchAttempt((prev) => prev + 1);
  };

  // Handle hard refresh detection
  useEffect(() => {
    if (disableAutoRefresh) return;

    const handleHardRefresh = () => {
      // Check if this is likely a hard refresh
      // performance.navigation.type === 1 indicates a reload
      const isHardRefresh =
        // Modern browsers - check navigation type
        (window.performance &&
          window.performance.getEntriesByType &&
          window.performance.getEntriesByType("navigation").length > 0 &&
          (
            window.performance.getEntriesByType(
              "navigation",
            )[0] as PerformanceNavigationTiming
          ).type === "reload") ||
        // Legacy approach
        (window.performance &&
          "navigation" in window.performance &&
          (window.performance as { navigation: { type: number } }).navigation
            .type === 1);

      if (isHardRefresh) {
        console.log("Hard refresh detected, clearing cache");
        browserCache.set("json_doc_content", null);
        // No need to update fetchAttempt here as the component will remount anyway
      }
    };

    // Listen for page load which happens after a refresh
    window.addEventListener("load", handleHardRefresh);

    return () => {
      window.removeEventListener("load", handleHardRefresh);
    };
  }, [disableAutoRefresh]);

  useEffect(() => {
    // Check cache first (skip if retry attempt)
    if (fetchAttempt === 0) {
      const cachedContent = browserCache.get<JsonContentState>(
        "json_doc_content",
        cacheTimeMinutes,
      );
      if (cachedContent) {
        setJsonContent(cachedContent);
        return;
      }
    }

    // Wait until both JSON files are processed
    if (termsLoading || privacyLoading) {
      setJsonContent((prev) => ({ ...prev, isLoading: true }));
      return;
    }

    // Handle any errors
    const error = termsError || privacyError;
    if (error) {
      setJsonContent({
        terms: { html: "" },
        privacy: { html: "" },
        isLoading: false,
        error,
      });
      return;
    }

    // Create new content with processed HTML
    const newContent: JsonContentState = {
      terms: termsContent,
      privacy: privacyContent,
      isLoading: false,
      error: null,
    };

    // Save to cache and update state
    browserCache.set("json_doc_content", newContent);
    setJsonContent(newContent);
  }, [
    termsContent,
    privacyContent,
    termsLoading,
    privacyLoading,
    termsError,
    privacyError,
    cacheTimeMinutes,
    fetchAttempt,
  ]);

  return {
    jsonContent,
    isLoading: jsonContent.isLoading,
    error: jsonContent.error,
    clearCache,
  };
}
